import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Calendar, Plus, Trash2, Home, Move, Scissors, Sprout, RotateCw, Search, ChevronRight, X, Info, MapPin, Thermometer, Cloud, Gauge, ShieldCheck, AlertTriangle, Check } from 'lucide-react';
import { loadWithCache } from '@/lib/offlineCache';
import { OfflineBanner } from '@/components/OfflineBanner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ml = (m) => MONTHS[(m - 1) % 12];

const KIND = {
  sow_indoors: { Icon: Home, color: '#C9A85B', label: 'Start seeds' },
  direct_sow: { Icon: Sprout, color: '#4E7A33', label: 'Direct sow' },
  transplant: { Icon: Move, color: '#1A4331', label: 'Plant out' },
  harvest: { Icon: Scissors, color: '#C66B4A', label: 'Harvest' },
};

// 3-state planting windows (HUD glow): ideal / risky / avoid
const WIN = {
  optimal: { ring: '#6B9F4A', bg: 'rgba(107,159,74,0.16)', text: '#2F5222' },
  marginal: { ring: '#C9A85B', bg: 'rgba(201,168,91,0.20)', text: '#7A5F27' },
  unsuitable: { ring: '#C66B4A', bg: 'rgba(198,107,74,0.14)', text: '#9A4A30' },
};
const WIN_LEGEND = [['optimal', 'Ideal'], ['marginal', 'Risky'], ['unsuitable', 'Avoid']];
const CONF = {
  high: { c: '#6B9F4A', l: 'High confidence' },
  'medium-high': { c: '#6B9F4A', l: 'Good confidence' },
  medium: { c: '#C9A85B', l: 'Fair confidence' },
  estimate: { c: '#8EAA97', l: 'Seasonal estimate' },
};
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; } };

function ConfidenceChip({ level }) {
  const c = CONF[level] || CONF.estimate;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${c.c}22`, color: c.c }} data-testid="confidence-chip">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c.c }} /> {c.l}
    </span>
  );
}

// Discreet "how we work out this harvest date" explainer — keeps the existing
// layout, just a small ⓘ that opens a premium, plain-English breakdown of the
// calculation (your region & altitude, the live forecast, the heat-units method
// and the confidence). No layout/typography/colour change to the planner itself.
const EXP_ICON = { location: MapPin, heat: Thermometer, forecast: Cloud, pace: Gauge, confidence: ShieldCheck, calendar: Calendar };
const EXP_COLOR = { location: '#3F6C97', heat: '#C66B4A', forecast: '#4E7A33', pace: '#B0934E', confidence: '#4E7A33', calendar: '#7A6B57' };

function GddInfo({ est }) {
  const [open, setOpen] = useState(false);
  if (!est) return null;
  const lines = Array.isArray(est.explainer) && est.explainer.length ? est.explainer : null;
  const isGdd = (est.method || 'gdd') === 'gdd';
  const conf = est.confidence || est.model_confidence || 'moderate';
  return (
    <span className="relative inline-flex">
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid="gdd-info-button" aria-label="How this is worked out"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[#4E7A33] transition-transform active:scale-90" style={{ background: 'rgba(107,159,74,0.14)' }}>
        <Info className="w-3 h-3" />
      </button>
      {open && (
        <>
          <span className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-7 z-30 w-80 max-w-[86vw] max-h-[340px] overflow-y-auto rounded-2xl p-4 text-left shadow-2xl" style={{ background: '#FFFFFF', border: '1px solid rgba(26,67,49,0.12)' }} data-testid="gdd-info-panel">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#4E7A33]">How we worked this out</p>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" className="text-[#9A8F7E] hover:text-[#1A4331] transition-colors"><X className="w-3.5 h-3.5" /></button>
            </div>
            {lines ? (
              <div className="space-y-2.5">
                {lines.map((ln, i) => {
                  const IconC = EXP_ICON[ln.icon] || Info;
                  const c = EXP_COLOR[ln.icon] || '#4E7A33';
                  return (
                    <div key={i} className="flex gap-2.5">
                      <span className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${c}1A` }}>
                        <IconC className="w-3.5 h-3.5" style={{ color: c }} />
                      </span>
                      <p className="text-[12px] text-[#4A554A] leading-relaxed flex-1">{ln.text}</p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                {isGdd ? (
                  <p className="text-[12px] text-[#4A554A] leading-relaxed">We grow this crop forward using Growing Degree Days — the daily warmth above its base of <b>{est.base_temp}°C</b> (capped at {est.upper_cap}°C), refined by your live local forecast when you sow near-term.</p>
                ) : (
                  <p className="text-[12px] text-[#4A554A] leading-relaxed">{est.gdd_note || "This crop's timing is calendar-based, not heat-driven, so we use its typical days-to-harvest for your climate zone."}</p>
                )}
                <p className="text-[10px] text-[#9A8F7E] mt-2">Model confidence: <b>{conf}</b>. Based on NZ-relevant horticultural degree-day references.</p>
              </>
            )}
          </div>
        </>
      )}
    </span>
  );
}

// Two side-by-side stat tiles: Growing energy (GDD budget) + Until harvest (DTH).
// Same frosted HUD styling as before — just the single "Est. harvest" line split
// into two distinct glanceable stats, with the confidence chip + ⓘ kept in the header.
function HarvestStats({ est, testId, limitation }) {
  if (!est) return null;
  const isGdd = (est.method || 'gdd') === 'gdd' && est.gdd_target;
  return (
    <div className="mt-3 rounded-2xl p-3" style={{ background: 'rgba(107,159,74,0.08)', border: '1px solid rgba(107,159,74,0.2)' }} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-[#4E7A33]">Harvest outlook</span>
        <div className="flex items-center gap-1.5">
          <ConfidenceChip level={est.confidence} />
          <GddInfo est={est} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-xl px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.65)' }} data-testid={`${testId}-gdd`}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#7A6B57]">Growing energy</p>
          {isGdd ? (
            <>
              <p className="text-lg font-bold text-[#1A4331] leading-tight mt-0.5">~{est.gdd_target}<span className="text-[11px] font-semibold text-[#4E7A33] ml-1">GDD</span></p>
              <p className="text-[10px] text-[#7A6B57]">base {est.base_temp}°C</p>
            </>
          ) : (
            <>
              <p className="text-lg font-bold text-[#1A4331] leading-tight mt-0.5">Calendar</p>
              <p className="text-[10px] text-[#7A6B57]">timed by season</p>
            </>
          )}
        </div>
        <div className="rounded-xl px-3 py-2.5" style={{ background: 'rgba(255,255,255,0.65)' }} data-testid={`${testId}-dth`}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#7A6B57]">Until harvest</p>
          <p className="text-lg font-bold text-[#1A4331] leading-tight mt-0.5">{est.days_to_harvest}<span className="text-[11px] font-semibold text-[#4E7A33] ml-1">days</span></p>
          {est.harvest_window
            ? <p className="text-[10px] text-[#7A6B57]">{fmtDate(est.harvest_window.earliest)} – {fmtDate(est.harvest_window.latest)} · ~{fmtDate(est.harvest_date)}</p>
            : <p className="text-[10px] text-[#7A6B57]">~{fmtDate(est.harvest_date)}</p>}
        </div>
      </div>
      {limitation ? <p className="text-[11px] text-[#7A6B57] mt-2">{limitation}</p> : null}
    </div>
  );
}

function WindowStrip({ windows }) {
  if (!windows || windows.length === 0) return null;
  const hasTp = windows.some((w) => w.is_transplant);
  const tpColor = KIND.transplant.color;
  return (
    <div className="mt-4" data-testid="window-strip">
      <div className="flex gap-1">
        {windows.map((w) => {
          const s = WIN[w.state] || WIN.unsuitable;
          return (
            <div key={w.month} title={`${w.month_label}: ${w.reason}${w.is_transplant ? ' · Plant out seedlings' : ''}`}
              className="relative flex-1 rounded-md text-center py-1 overflow-hidden"
              data-testid={w.is_transplant ? `window-transplant-${w.month}` : undefined}
              style={{ background: s.bg, boxShadow: w.is_current ? `0 0 0 2px ${s.ring}, 0 0 10px ${s.ring}66` : `inset 0 0 0 1px ${s.ring}55` }}>
              <span className="text-[9px] font-bold" style={{ color: s.text }}>{w.month_label[0]}</span>
              {w.is_transplant && <span className="absolute left-0 right-0 bottom-0 h-[3px]" style={{ background: tpColor }} />}
            </div>
          );
        })}
      </div>
      <div className="flex gap-3 mt-1.5 flex-wrap">
        {WIN_LEGEND.map(([k, l]) => (
          <span key={k} className="flex items-center gap-1 text-[10px] text-[#7A6B57]">
            <span className="w-2 h-2 rounded-full" style={{ background: WIN[k].ring }} /> {l}
          </span>
        ))}
        {hasTp && (
          <span className="flex items-center gap-1 text-[10px] text-[#7A6B57]" data-testid="window-legend-transplant">
            <Move className="w-2.5 h-2.5" style={{ color: tpColor }} /> Plant out
          </span>
        )}
      </div>
    </div>
  );
}

function SeasonPlanner({ user }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const kindLabel = (k) => t('season.kind_' + (KIND[k] ? k : 'harvest'));
  const [data, setData] = useState(null);
  const [rotation, setRotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [tab, setTab] = useState('plan');
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    try {
      const { data: payload, offline: off, savedAt: sa } = await loadWithCache('season_plan', async () => {
        const [d, r] = await Promise.all([
          axios.get(`${API}/season-plan`, { withCredentials: true }),
          axios.get(`${API}/season-plan/rotation`, { withCredentials: true }).catch(() => ({ data: null })),
        ]);
        return { data: d.data, rotation: r.data };
      });
      setData(payload.data);
      setRotation(payload.rotation);
      setOffline(off);
      setSavedAt(sa);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const onOnline = () => load();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, []);

  const remove = async (id) => {
    try { await axios.delete(`${API}/season-plan/items/${id}`, { withCredentials: true }); load(); } catch (e) { console.error(e); }
  };

  if (loading) return <Layout user={user}><div className="flex items-center justify-center h-96"><div className="w-16 h-16 border-4 border-[#8EAA97] border-t-[#1A4331] rounded-full animate-spin"></div></div></Layout>;

  const items = data?.items || [];
  const months = (data?.months || []).filter((m) => m.tasks.length > 0);

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {offline && <OfflineBanner savedAt={savedAt} onRetry={load} />}
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm text-[#7A6B57] font-medium uppercase tracking-wider">{t('season.seasonPlanner')}</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('season.planWholeSeason')}
            </h1>
            <p className="text-base text-[#7A6B57] mt-2 max-w-2xl">
              {t('season.headerDesc')}{data?.season_label ? ` · ${data.season_label}` : ''}.
            </p>
          </div>
          <button onClick={() => setAddOpen(true)} data-testid="plan-add-button" className="bg-[#1A4331] hover:bg-[#0F2D1F] text-[#FAF6EC] font-semibold py-3 px-6 rounded-full transition-colors flex items-center gap-2">
            <Plus className="w-5 h-5" /> {t('season.addCrop')}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#1A4331]/10">
          {[{ id: 'plan', label: `${t('season.myPlan')} (${items.length})` }, { id: 'months', label: t('season.byMonth') }].map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)} data-testid={`tab-${tb.id}`}
              className={`px-4 py-3 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === tb.id ? 'border-[#1A4331] text-[#1A4331]' : 'border-transparent text-[#7A6B57] hover:text-[#1A4331]'}`}>
              {tb.label}
            </button>
          ))}
        </div>

        {/* Plan tab */}
        {tab === 'plan' && (
          items.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-[#1A4331]/5 shadow-sm">
              <Calendar className="w-14 h-14 text-[#8EAA97] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#2D241E] mb-2">{t('season.planYourSeason')}</h3>
              <p className="text-sm text-[#5C665C] mb-6 max-w-md mx-auto">{t('season.emptyDesc')}</p>
              <button onClick={() => setAddOpen(true)} className="bg-[#1A4331] hover:bg-[#0F2D1F] text-white font-semibold py-3 px-8 rounded-full transition-colors">{t('season.addCrop')}</button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {items.map((it) => (
                <div key={it.item_id} data-testid={`plan-item-${it.item_id}`} className="bg-white rounded-3xl p-6 border border-[#1A4331]/5 shadow-sm">
                  <div className="flex items-start justify-between">
                    <button onClick={() => navigate(`/plant-today/${it.crop_id}`)} data-testid={`plan-plant-today-${it.crop_id}`} className="text-left group">
                      <h3 className="text-2xl font-bold text-[#1A4331] group-hover:text-[#0F2D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>{it.crop_name}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${it.method === 'seedling' ? 'bg-[#8FB4D6]/15 text-[#3F6C97]' : 'bg-[#6B9F4A]/15 text-[#4E7A33]'}`}>{it.method === 'seedling' ? t('season.fromSeedling') : t('season.fromSeed')}</span>
                        {it.category && <span className="text-xs text-[#7A6B57]">{it.category}</span>}
                      </div>
                      <span className="inline-flex items-center gap-1 mt-2 text-[12px] font-bold" style={{ color: '#6B9F4A' }}>Plant today <ChevronRight className="w-3.5 h-3.5" /></span>
                    </button>
                    <button onClick={() => remove(it.item_id)} data-testid={`plan-delete-${it.item_id}`} className="text-[#8EAA97] hover:text-[#C66B4A] transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-4 space-y-3">
                    {it.timeline.steps.map((s, i) => {
                      const meta = KIND[s.kind] || KIND.harvest;
                      const StepIcon = meta.Icon;
                      return (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${meta.color}22` }}>
                            <StepIcon className="w-4 h-4" style={{ color: meta.color }} />
                          </div>
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider" style={{ color: meta.color }}>{ml(s.month)} · {kindLabel(s.kind)}</p>
                            <p className="text-sm text-[#2A3528]">{s.action}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {it.timeline?.harvest_estimate && (
                    <HarvestStats est={it.timeline.harvest_estimate} testId={`harvest-estimate-${it.item_id}`} limitation={it.timeline.limitation} />
                  )}
                  <WindowStrip windows={it.windows} />
                  {it.succession?.suitable && (
                    <div className="mt-3 flex items-center gap-1.5 text-[11px] font-medium" style={{ color: '#4E7A33' }} data-testid={`succession-hint-${it.item_id}`}>
                      <RotateCw className="w-3 h-3 flex-shrink-0" />
                      <span>{it.succession.hint}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}

        {/* By-month tab */}
        {tab === 'months' && (
          months.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-[#1A4331]/5 shadow-sm">
              <Calendar className="w-14 h-14 text-[#8EAA97] mx-auto mb-4" />
              <p className="text-[#7A6B57]">{t('season.monthsEmpty')}</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {months.map((m) => (
                <div key={m.month} data-testid={`month-${m.month}`} className={`bg-white rounded-3xl p-6 border shadow-sm ${m.is_current ? 'border-[#6B9F4A]' : 'border-[#1A4331]/5'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{ml(m.month)}</h3>
                    {m.is_current && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#6B9F4A]/15 text-[#4E7A33]">{t('season.thisMonth')}</span>}
                  </div>
                  <ul className="space-y-2">
                    {m.tasks.map((t, i) => {
                      const meta = KIND[t.kind] || KIND.harvest;
                      const StepIcon = meta.Icon;
                      return (
                        <li key={i} className="flex items-start gap-2">
                          <StepIcon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: meta.color }} />
                          <span className="text-sm text-[#2A3528]">{t.action}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )
        )}

        {/* Rotation guide */}
        {rotation && (
          <div className="bg-gradient-to-br from-[#8EAA97]/10 to-[#FAF6EC] rounded-3xl p-6 border border-[#8EAA97]/20" data-testid="rotation-guide">
            <div className="flex items-center gap-2 mb-1">
              <RotateCw className="w-5 h-5 text-[#1A4331]" />
              <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('season.cropRotationGuide')}</h3>
            </div>
            <p className="text-sm text-[#5C665C] mb-4">{t('season.rotationDesc')}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {(rotation.rotation_order || []).map((s, i) => {
                const isNext = rotation.next_stage === (i + 1);
                return (
                  <div key={s.key} className={`flex items-center gap-3 rounded-2xl p-3 ${isNext ? 'bg-[#6B9F4A]/10 border border-[#6B9F4A]/30' : 'bg-white/60'}`}>
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isNext ? 'bg-[#1A4331] text-white' : 'bg-[#E5EBDD] text-[#7A6B57]'}`}>{i + 1}</span>
                    <div className="flex-1">
                      <p className="font-semibold text-[#1A4331] text-sm">{s.label}</p>
                      <p className="text-xs text-[#7A6B57]">{s.examples}</p>
                    </div>
                    {isNext && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#6B9F4A]/15 text-[#4E7A33]">{t('season.startHere')}</span>}
                  </div>
                );
              })}
            </div>
            {rotation.suggestion && <p className="text-sm text-[#2A3528] italic mt-3">{rotation.suggestion.benefit}</p>}
          </div>
        )}

        <p className="text-[11px] leading-relaxed mt-6 max-w-2xl text-[#8EAA97]" data-testid="planting-disclaimer">
          HomeGrown's planting and harvesting recommendations are guides based on available horticultural information and regional growing conditions. Actual results vary with weather, soil, crop variety and garden management. While we strive for accuracy, successful growth or harvest cannot be guaranteed. Nothing in this guidance limits rights under applicable New Zealand law.
        </p>
      </div>

      {addOpen && <AddCropModal onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
    </Layout>
  );
}

function AddCropModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const [crops, setCrops] = useState([]);
  const [beds, setBeds] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [method, setMethod] = useState('seed');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [bedId, setBedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [windowInfo, setWindowInfo] = useState(null);
  const [addSuccession, setAddSuccession] = useState(false);

  useEffect(() => {
    axios.get(`${API}/crops`).then((r) => setCrops(r.data || [])).catch(() => {});
    (async () => {
      try {
        const g = await axios.get(`${API}/gardens`, { withCredentials: true });
        const all = [];
        for (const garden of g.data || []) {
          const bs = await axios.get(`${API}/beds`, { params: { garden_id: garden.garden_id }, withCredentials: true });
          (bs.data || []).forEach((b) => all.push({ ...b, garden_id: garden.garden_id }));
        }
        setBeds(all);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // Fetch the zone-adjusted viable window whenever crop or method changes
  useEffect(() => {
    if (!selected) { setWindowInfo(null); return; }
    let active = true;
    axios.get(`${API}/season-plan/crop-window`, { params: { crop_id: selected.crop_id, method, target_month: month }, withCredentials: true })
      .then((r) => { if (active) setWindowInfo(r.data); })
      .catch(() => { if (active) setWindowInfo(null); });
    return () => { active = false; };
  }, [selected, method, month]);

  // Reset the "add succession" opt-in whenever the grower picks a different crop.
  useEffect(() => { setAddSuccession(false); }, [selected]);

  const winByMonth = {};
  (windowInfo?.windows || []).forEach((w) => { winByMonth[w.month] = w; });
  const preview = windowInfo?.harvest_estimate || null;

  const viable = windowInfo?.viable_months || [];
  const notSuited = !!windowInfo?.not_suited;
  const unrestricted = !windowInfo || windowInfo.year_round || (viable.length === 0 && !notSuited);
  const monthAllowed = (m) => unrestricted || viable.includes(m);
  const monthValid = monthAllowed(month);
  const reason = notSuited
    ? windowInfo.climate_note
    : (windowInfo && !monthValid)
    ? `In your area (${windowInfo.region_label || windowInfo.zone_label}), ${selected?.name} is best ${windowInfo.verb}${windowInfo.material ? ` from ${windowInfo.material}` : ''} in ${windowInfo.suggested_range}. ${MONTHS[month - 1]} is outside that window.${/sensit/i.test(windowInfo.frost_tolerance || '') ? " It's frost-sensitive — a cold-season planting would likely be lost." : ''}`
    : null;

  const filtered = crops.filter((c) => !search || c.name.toLowerCase().includes(search.toLowerCase())).slice(0, 40);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.post(`${API}/season-plan/items`, {
        crop_id: selected.crop_id, method, target_month: month,
        bed_id: bedId, garden_id: bedId ? beds.find((b) => b.bed_id === bedId)?.garden_id : null,
        override: !monthValid,
      }, { withCredentials: true });
      if (addSuccession && windowInfo?.succession?.count > 0) {
        await axios.post(`${API}/season-plan/items/succession`, {
          crop_id: selected.crop_id, method, target_month: month,
          bed_id: bedId, garden_id: bedId ? beds.find((b) => b.bed_id === bedId)?.garden_id : null,
        }, { withCredentials: true }).catch(() => {});
      }
      onSaved();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FAF6EC] rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="plan-add-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{selected ? t('season.planCrop', { name: selected.name }) : t('season.addCrop')}</h3>
          <button onClick={onClose} className="text-[#7A6B57] hover:text-[#1A4331]"><X className="w-5 h-5" /></button>
        </div>

        {!selected ? (
          <>
            <div className="flex items-center gap-2 bg-white rounded-xl border border-[#1A4331]/10 px-4 mb-3">
              <Search className="w-4 h-4 text-[#7A6B57]" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('season.searchCatalogue')} data-testid="plan-crop-search" className="w-full py-3 bg-transparent outline-none" />
            </div>
            <div className="max-h-[50vh] overflow-y-auto divide-y divide-[#1A4331]/5">
              {filtered.map((c) => (
                <button key={c.crop_id} onClick={() => setSelected(c)} data-testid={`plan-crop-${c.crop_id}`} className="w-full flex items-center gap-3 py-3 text-left hover:bg-white/50 rounded-xl px-2">
                  <div className="w-9 h-9 rounded-full bg-[#6B9F4A]/15 flex items-center justify-center flex-shrink-0"><Sprout className="w-4 h-4 text-[#4E7A33]" /></div>
                  <div className="flex-1">
                    <p className="font-semibold text-[#1A4331]">{c.name}</p>
                    <p className="text-xs text-[#7A6B57]">{c.category}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#8EAA97]" />
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#2D241E] mb-2">{t('season.howGrow')}</label>
              <div className="flex gap-2">
                {['seed', 'seedling'].map((m) => (
                  <button key={m} onClick={() => setMethod(m)} data-testid={`method-${m}`} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${method === m ? 'bg-[#1A4331] text-white' : 'bg-white text-[#1A4331] border border-[#1A4331]/10'}`}>
                    {m === 'seed' ? t('season.fromSeed') : t('season.fromSeedling')}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#2D241E] mb-2">{method === 'seedling' ? t('season.plantOutMonth') : t('season.targetMonth')}</label>
              {windowInfo && !unrestricted && !notSuited && (
                <p className="text-xs text-[#4E7A33] mb-2 flex items-center gap-1.5" data-testid="window-hint">
                  <Sprout className="w-3.5 h-3.5" /> In {windowInfo.region_label || windowInfo.zone_label}, {windowInfo.verb} {selected.name}{windowInfo.material ? ` from ${windowInfo.material}` : ''} in <span className="font-semibold">{windowInfo.suggested_range}</span>. Other months need cover (e.g. a greenhouse) — tap to add anyway.
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                {MONTHS.map((mm, i) => {
                  const m = i + 1;
                  const allowed = monthAllowed(m);
                  const w = winByMonth[m];
                  const s = w ? WIN[w.state] : null;
                  const isSel = month === m;
                  return (
                    <button key={mm} onClick={() => setMonth(m)} data-testid={`month-chip-${m}`} data-state={w ? w.state : (allowed ? 'optimal' : 'unsuitable')} title={w ? w.reason : ''}
                      className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${isSel ? 'text-white' : ''} ${!s && !allowed ? 'line-through' : ''}`}
                      style={isSel
                        ? { background: s ? s.ring : '#1A4331', boxShadow: s ? `0 0 10px ${s.ring}77` : 'none' }
                        : s ? { background: s.bg, color: s.text, boxShadow: `inset 0 0 0 1px ${s.ring}66` }
                            : { background: allowed ? '#fff' : '#EFEBDD', color: allowed ? '#1A4331' : '#B9B29F', border: '1px solid rgba(26,67,49,0.1)' }}>
                      {mm}
                    </button>
                  );
                })}
              </div>
              {windowInfo?.windows && (
                <div className="flex gap-3 mt-2" data-testid="window-legend">
                  {WIN_LEGEND.map(([k, l]) => (
                    <span key={k} className="flex items-center gap-1 text-[10px] text-[#7A6B57]">
                      <span className="w-2 h-2 rounded-full" style={{ background: WIN[k].ring }} /> {l}
                    </span>
                  ))}
                </div>
              )}
              {preview && (
                <HarvestStats est={preview} testId="harvest-preview" />
              )}
              {windowInfo?.succession?.suitable && windowInfo.succession.count > 0 && (
                <div className="mt-3 rounded-2xl p-3" style={{ background: 'rgba(107,159,74,0.08)', border: '1px solid rgba(107,159,74,0.25)' }} data-testid="succession-suggest">
                  <div className="flex items-start gap-2.5">
                    <span className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(107,159,74,0.18)' }}>
                      <RotateCw className="w-3.5 h-3.5" style={{ color: '#4E7A33' }} />
                    </span>
                    <div className="flex-1">
                      <p className="text-[11px] font-bold uppercase tracking-wider text-[#4E7A33]">Keep it producing</p>
                      <p className="text-sm text-[#2A3528] mt-0.5">{windowInfo.succession.blurb}</p>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid="succession-dates">
                        <span className="text-[11px] text-[#7A6B57]">Re-sow:</span>
                        {windowInfo.succession.sowings.map((s) => (
                          <span key={s.month} className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(107,159,74,0.15)', color: '#4E7A33' }}>{s.month_label}</span>
                        ))}
                      </div>
                      <button type="button" onClick={() => setAddSuccession((v) => !v)} data-testid="succession-toggle"
                        className="flex items-center gap-2 mt-2.5 text-[12px] font-semibold text-[#1A4331]">
                        <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ border: '1.5px solid #4E7A33', background: addSuccession ? '#4E7A33' : 'transparent' }}>
                          {addSuccession && <Check className="w-3 h-3 text-white" />}
                        </span>
                        Also add {windowInfo.succession.count} follow-on sowing{windowInfo.succession.count > 1 ? 's' : ''} to my plan
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {reason && (
              <div className="rounded-2xl p-4 flex items-start gap-3" style={{ background: 'rgba(198,107,74,0.10)', border: '1.5px solid rgba(198,107,74,0.55)' }} data-testid="window-warning">
                <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#C66B4A' }}>
                  <AlertTriangle className="w-4 h-4 text-white" />
                </span>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#C66B4A]">{notSuited ? 'Not recommended outdoors in your climate' : 'Outside your recommended planting window'}</p>
                  <p className="text-sm text-[#7A3E2C] mt-1">{reason}</p>
                  <p className="text-sm text-[#7A3E2C] mt-1">Planting now carries an increased risk of poor growth or crop failure. Plant at your own risk.</p>
                </div>
              </div>
            )}
            {beds.length > 0 && (
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-2">{t('season.assignBed')}</label>
                <div className="flex flex-wrap gap-2">
                  {beds.map((b) => (
                    <button key={b.bed_id} onClick={() => setBedId(bedId === b.bed_id ? null : b.bed_id)} className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${bedId === b.bed_id ? 'bg-[#1A4331] text-white' : 'bg-white text-[#1A4331] border border-[#1A4331]/10'}`}>{b.name}</button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <button onClick={() => setSelected(null)} className="flex-1 border-2 border-[#8EAA97] text-[#1A4331] py-3 rounded-full font-semibold">{t('common.back')}</button>
              <button onClick={submit} disabled={saving} data-testid="plan-save-button" className={`flex-1 py-3 rounded-full font-semibold disabled:opacity-50 text-white ${monthValid ? 'bg-[#1A4331] hover:bg-[#0F2D1F]' : 'bg-[#C66B4A] hover:bg-[#B4573B]'}`}>{saving ? t('season.adding') : (monthValid ? t('season.addToPlan') : (notSuited ? 'Plant under cover' : 'Plant anyway'))}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SeasonPlanner;
